import { useCallback, useEffect, useState } from "react";

const LOCALSTORAGE_KEY = "featureFlags";

// All flags are false by default.
// If you want to enable a flag in dev, use the hook, or manually edit the
// "featureFlags" value in localStorage.
const FEATURE_FLAGS = {
  headcount: false,
  createServiceFromDocument: false,
  copilot: false,
};

export type FeatureFlags = keyof typeof FEATURE_FLAGS;

// On application startup, add flags to local storage
// Respect existing flags in local storage
if (!localStorage.getItem(LOCALSTORAGE_KEY)) {
  localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(FEATURE_FLAGS));
} else {
  // keep local storage flags up to date with FEATURE_FLAGS while maintaining the user's settings
  const localStorageFlags = JSON.parse(
    localStorage.getItem(LOCALSTORAGE_KEY) as string,
  );

  for (const key in FEATURE_FLAGS) {
    if (!(key in localStorageFlags)) {
      localStorageFlags[key] = FEATURE_FLAGS[key as keyof typeof FEATURE_FLAGS];
    }
  }

  for (const key in localStorageFlags) {
    if (!(key in FEATURE_FLAGS)) {
      delete localStorageFlags[key];
    }
  }

  localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(localStorageFlags));
}

export function useFeatureFlags(): {
  getFlag: (key: FeatureFlags) => boolean;
  setFlag: (key: FeatureFlags, value: boolean) => void;
} {
  const [flags, setFlags] = useState<{ [key in FeatureFlags]: boolean }>(
    (): typeof FEATURE_FLAGS => {
      const storedFlags = localStorage.getItem(LOCALSTORAGE_KEY) || "{}";

      try {
        return JSON.parse(storedFlags);
      } catch (e) {
        return FEATURE_FLAGS;
      }
    },
  );

  const getFlag = useCallback(
    (key: FeatureFlags) => {
      return flags[key] ?? false;
    },
    [flags],
  );

  const setFlag = useCallback(
    (key: FeatureFlags, active: boolean) => {
      const newFlags = { ...flags };
      newFlags[key] = active;
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(flags));
      setFlags(newFlags);
    },
    [flags],
  );

  const onStorageChange = useCallback((event: StorageEvent) => {
    if (event.key === LOCALSTORAGE_KEY && event.newValue) {
      setFlags(JSON.parse(event.newValue));
    }
  }, []);

  // Listen for changes to local storage
  // This will only fire when localStorage is edited manually, not when
  // set by this hook.
  useEffect(() => {
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    getFlag,
    setFlag,
  };
}
